import React from 'react';
import Page from 'framework7-react/components/page';
import {
  Navbar,
  NavRight,
  NavLeft,
  NavTitle,
  Icon,
  Link,
  Fab,
  Button,
  List,
  Card
} from 'framework7-react';
import Footer from '../common/Footer';
import II18 from '../translate/II18';
import {
  getRequestInfoFromUrl,
  getUsedTemplates,
  isAppOnline,
  prepareApp,
  APP_ONLINE_EVENT,
  APP_OFFLINE_EVENT,
  removeUsedTemplates,
  REQUEST_TYPE,
  updateUsedTemplates,
  isTemplateIgnored,
  addIgnoredTemplate
} from '../../lib/utils';
import {
  getCachedTemplate,
  isNetworkUnavailable,
  isTemplateNotFoundResponse,
  saveCachedTemplate
} from '../../lib/templateCache';
import {
  clearStandaloneDraft,
  getStandaloneDraft,
  getStandaloneDraftForNewSession,
  hasPendingStandaloneDraftRestore,
  markPendingStandaloneDraftRestore
} from '../../lib/standaloneDraftCache';
import { gaNavigateBack } from '../home/ga';
import { gaeApp } from '../helpers/Analytics';
import PropTypes from 'prop-types';
import { noteActiveRequestId } from '../../telemetry/tracing';
import { f7navigator, initF7Navigator } from '../helpers/navigator';
import PaperflyAPI from '../../server/api/PaperflyAPI';
import Spinner from '../helpers/Spinner';
import Scanner from './Scanner';
import InstallUserGuid from './InstallUserGuid';
import TemplatesList from './TemplatesList';
import StandaloneDraftNotification from './StandaloneDraftNotification';
import DesktopStartRequestFab from './DesktopStartRequestFab';
import ListInput from '../customF7/list-input/list-input';
import { getIcon } from '../helpers/utils';
import { getServiceWorkerStatus } from '../../serviceWorker';

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

class StartFromQR extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      recipientRequestId: null,
      requestId: '',
      scannerOn: false,
      fetchingTemplateInfo: false,
      draftVersion: 0,
      pendingDraft: null,
      installGuideOpened: false
    };
  }

  refreshDraftIndicator = () => {
    this.setState(state => ({ draftVersion: state.draftVersion + 1 }));
  };

  templateInfoFromResponse(templateId, response) {
    if (
      !response ||
      response.errorCode ||
      isTemplateNotFoundResponse(response)
    ) {
      return null;
    }
    return {
      id: templateId,
      name: response.caseName,
      company: response.requester,
      logo: response.requesterLogoUrl
    };
  }

  getTemplateInfo(templateId, onDone) {
    const finishFromCache = reason => {
      gaeApp('TEMPLATE_CACHE', 'GET_INFO_USE_CACHE', `${reason}:${templateId}`);
      getCachedTemplate(templateId).then(cached =>
        onDone(this.templateInfoFromResponse(templateId, cached), cached, true)
      );
    };

    const fetchFromNetwork = () => {
      gaeApp('TEMPLATE_CACHE', 'GET_INFO_USE_NETWORK', templateId);
      PaperflyAPI.listRequestedDocumentsTemplate(templateId).then(
        response => {
          onDone(
            this.templateInfoFromResponse(templateId, response),
            response,
            false
          );
        },
        error => {
          const data = error?.response?.data;
          if (
            isTemplateNotFoundResponse(data) ||
            isTemplateNotFoundResponse({
              message: data?.error || data?.message || error?.message
            })
          ) {
            gaeApp('TEMPLATE_CACHE', 'GET_INFO_NOT_FOUND', templateId);
            onDone(null, data || { message: error?.message }, false);
            return;
          }
          if (isNetworkUnavailable(error)) {
            finishFromCache('NETWORK_ERROR');
          } else {
            gaeApp('TEMPLATE_CACHE', 'GET_INFO_FAILED', templateId);
            onDone(null, null, false);
          }
        }
      );
    };

    if (!isAppOnline()) {
      finishFromCache('OFFLINE');
      return;
    }

    prepareApp(fetchFromNetwork, () => {
      gaeApp('TEMPLATE_CACHE', 'GET_INFO_PREPARE_FAILED', templateId);
      finishFromCache('PREPARE_FAILED');
    });
  }

  openTemplateNotFoundDialog = templateId => {
    this.$f7.dialog
      .create({
        title: II18.get('common_error'),
        text: II18.get('qr_start_template_not_found'),
        closeByBackdropClick: false,
        verticalButtons: true,
        buttons: [
          {
            text: II18.get('title_delete'),
            bold: true,
            onClick: () => {
              gaeApp('TEMPLATE_CACHE', 'DELETE_MISSING', templateId);
              this.removeTemplates([templateId]);
            }
          },
          {
            text: II18.get('qr_start_template_later'),
            onClick: () => {
              gaeApp('TEMPLATE_CACHE', 'KEEP_MISSING', templateId);
            }
          }
        ]
      })
      .open();
  };

  componentDidMount() {
    initF7Navigator(this.$f7router);

    window.addEventListener(APP_ONLINE_EVENT, this.onAppOnlineReady);
    window.addEventListener(APP_OFFLINE_EVENT, this.onConnectivityChange);
    window.addEventListener('online', this.onConnectivityChange);
    window.addEventListener('offline', this.onConnectivityChange);

    this.promptLaunchTemplateFromUrl();
    this.promptStandaloneDraftRestore();
  }

  /**
   * Installed app opened with /t=<id> (iOS home-screen URL or dynamic
   * manifest start_url). Offer Start / Save / Ignore when the template is
   * neither saved nor ignored yet.
   */
  promptLaunchTemplateFromUrl = () => {
    if (!window.standalone || window.standaloneLaunchTemplatePrompted) return;
    window.standaloneLaunchTemplatePrompted = true;

    const requestInfo =
      this.props.requestInfo?.requestType && this.props.requestInfo?.requestId
        ? this.props.requestInfo
        : getRequestInfoFromUrl();

    if (
      requestInfo.requestType !== REQUEST_TYPE.TEMPLATE ||
      !requestInfo.requestId
    ) {
      return;
    }

    const templateId = requestInfo.requestId;
    const alreadySaved = getUsedTemplates().some(
      item => item.id === templateId
    );
    if (alreadySaved || isTemplateIgnored(templateId)) {
      return;
    }

    this.setState({ fetchingTemplateInfo: true });
    this.getTemplateInfo(templateId, (templateInfo, response) => {
      this.setState({ fetchingTemplateInfo: false });
      if (!templateInfo) {
        gaeApp('LAUNCH_TEMPLATE', 'NOT_FOUND', templateId);
        return;
      }
      this.pendingTemplateResponse = response;
      gaeApp('LAUNCH_TEMPLATE', 'PROMPT', templateId);
      this.scannedTemplateDialog(templateInfo, {
        dismissLabelKey: 'qr_start_ignore_template',
        onDismiss: id => {
          addIgnoredTemplate(id);
          gaeApp('LAUNCH_TEMPLATE', 'IGNORE', id);
        }
      });
    });
  };

  promptStandaloneDraftRestore = () => {
    if (!window.standalone || window.standaloneDraftPrompted) return;
    window.standaloneDraftPrompted = true;

    getStandaloneDraftForNewSession().then(draft => {
      if (!draft?.templateId) return;

      gaeApp('STANDALONE_DRAFT', 'PROMPT', draft.templateId);
      this.setState({ pendingDraft: draft });
    });
  };

  continuePendingDraft = () => {
    const { pendingDraft } = this.state;
    if (!pendingDraft?.templateId) return;

    gaeApp('STANDALONE_DRAFT', 'CONTINUE', pendingDraft.templateId);
    this.setState({ pendingDraft: null });
    markPendingStandaloneDraftRestore();
    this.startTemplate(pendingDraft.templateId);
  };

  dismissPendingDraft = () => {
    const { pendingDraft } = this.state;
    if (!pendingDraft?.templateId) return;

    gaeApp('STANDALONE_DRAFT', 'DISMISS', pendingDraft.templateId);
    this.setState({ pendingDraft: null });
  };

  componentWillUnmount() {
    window.removeEventListener(APP_ONLINE_EVENT, this.onAppOnlineReady);
    window.removeEventListener(APP_OFFLINE_EVENT, this.onConnectivityChange);
    window.removeEventListener('online', this.onConnectivityChange);
    window.removeEventListener('offline', this.onConnectivityChange);
  }

  onAppOnlineReady = () => {
    this.forceUpdate();
  };

  onConnectivityChange = () => {
    if (!isAppOnline() && this.state.scannerOn) {
      this.setState({ scannerOn: false });
    } else {
      this.forceUpdate();
    }
  };

  alertWrongRequest() {
    this.$f7.dialog.alert(II18.get('home_request_notexists'));
  }

  openInstallGuidePopup = () => {
    this.setState({ installGuideOpened: true });
  };

  openAboutDialog = () => {
    if (!window.standalone) {
      const dialog = this.$f7.dialog.create({
        title: 'Paperfly App',
        content: `<div style="text-align:center;padding:8px 0">
            <div>Version: ${escapeHtml(window.pwaVersion || '')}</div>
            <div style="margin-top:12px">${escapeHtml(
              II18.get('pwa_install_prompt_message')
            )}</div>
            <div style="margin-top:8px">
              <a href="#" class="install-guide-link">${escapeHtml(
                II18.get('pwa_install_prompt_link')
              )}</a>
            </div>
          </div>`,
        closeByBackdropClick: true,
        buttons: [
          {
            text: II18.get('title_close'),
            bold: true
          }
        ],
        on: {
          opened: dialogInstance => {
            const rootEl =
              dialogInstance?.el || dialogInstance?.$el?.[0] || dialogInstance;
            const link = rootEl?.querySelector?.('.install-guide-link');
            if (!link) return;
            link.onclick = event => {
              event.preventDefault();
              dialogInstance?.close?.();
              this.openInstallGuidePopup();
            };
          }
        }
      });

      dialog.open();
      return;
    }

    getServiceWorkerStatus().then(sw => {
      this.$f7.dialog
        .create({
          title: 'Paperfly App',
          content: `<div style="text-align:center;padding:8px 0">
              <div>Version: ${window.pwaVersion || ''}</div>
              <div style="margin-top:8px">Service worker:<br/>${sw.label}</div>
            </div>`,
          closeByBackdropClick: true,
          buttons: [
            {
              text: II18.get('standalone_draft_new_start'),
              onClick: () => {
                gaeApp('ABOUT.NEW_START');
                window.location.reload();
              }
            },
            {
              text: II18.get('title_close'),
              bold: true
            }
          ]
        })
        .open();
    });
  };

  scannedTemplateDialog(scannedTemplate, options = {}) {
    const { dismissLabelKey = 'title_cancel', onDismiss } = options;

    this.$f7.dialog
      .create({
        title: II18.get('qr_start_what_next'),
        cssClass: 'scannedTemplateDialogWrap',
        content: `<div class='scannedTemplateDialog'>
            <img alt='Logo' src='${scannedTemplate.logo}'/>
            <label>
                <span><b>${scannedTemplate.name}</b></span>
                <span>${scannedTemplate.company}</span>
            </label>
          </div>`,
        animate: true,
        closeByBackdropClick: false,
        verticalButtons: true,
        buttons: [
          {
            text: II18.get('qr_start_start_template'),
            onClick: () => {
              this.pendingTemplateResponse = null;
              this.startTemplate(scannedTemplate.id);
            }
          },
          {
            text: II18.get('qr_start_save_template'),
            onClick: () => this.saveTemplate(scannedTemplate)
          },
          {
            text: II18.get(dismissLabelKey),
            onClick: () => {
              this.pendingTemplateResponse = null;
              if (onDismiss) {
                onDismiss(scannedTemplate.id);
              } else {
                this.stopScanner();
              }
            }
          }
        ]
      })
      .open();
  }

  handleRequestData = data => {
    if (data) {
      this.stopScanner();

      if (data.indexOf('=') === -1) {
        this.startRequest({
          requestId: data,
          requestType: REQUEST_TYPE.COMMON_REQUEST
        });
        return;
      }

      const scannedRequestInfo = getRequestInfoFromUrl(data);

      if (scannedRequestInfo.requestType === REQUEST_TYPE.TEMPLATE) {
        if (window.standalone) {
          const templateId = scannedRequestInfo.requestId;
          this.setState({ fetchingTemplateInfo: true });

          this.getTemplateInfo(templateId, (templateInfo, response) => {
            if (!templateInfo) {
              this.pendingTemplateResponse = null;
              this.alertWrongRequest();
            } else {
              this.pendingTemplateResponse = response;
              this.scannedTemplateDialog(templateInfo);
            }
            this.setState({
              fetchingTemplateInfo: false
            });
          });
        } else {
          this.startTemplate(scannedRequestInfo.requestId);
        }
      } else if (
        scannedRequestInfo.requestType &&
        scannedRequestInfo.requestId
      ) {
        this.startRequest(scannedRequestInfo);
      } else this.alertWrongRequest();
    }
  };

  saveTemplate(templateInfo) {
    updateUsedTemplates(templateInfo.id, templateInfo.name);
    if (
      this.pendingTemplateResponse &&
      !this.pendingTemplateResponse.errorCode
    ) {
      gaeApp('TEMPLATE_CACHE', 'SAVE_ON_USER_SAVE', templateInfo.id);
      saveCachedTemplate(templateInfo.id, this.pendingTemplateResponse);
    } else {
      gaeApp('TEMPLATE_CACHE', 'SAVE_SKIPPED_NO_PAYLOAD', templateInfo.id);
    }
    this.pendingTemplateResponse = null;
    this.forceUpdate();
  }

  removeTemplates = ids => {
    removeUsedTemplates(ids);
    this.forceUpdate();
  };

  startRequest(requestInfo) {
    if (requestInfo && requestInfo.requestId) {
      noteActiveRequestId(requestInfo.requestId, {
        'request.change_reason': 'start.request',
        'request.load_type': String(
          requestInfo.requestType || REQUEST_TYPE.COMMON_REQUEST
        )
      });
    }
    f7navigator.toLoadRequestFromQR(requestInfo);
  }

  startTemplate = (templateId, { fresh = false } = {}) => {
    if (window.standalone) {
      // Boot may have opened without /t=; enable draft once a template starts.
      window.draftEnabled = true;
    }
    if (templateId) {
      noteActiveRequestId(templateId, {
        'request.change_reason': 'start.template',
        'request.load_type': REQUEST_TYPE.TEMPLATE,
        'request.fresh': Boolean(fresh)
      });
    }
    const continueStart = () => {
      f7navigator.toLoadRequestFromQR({
        requestId: templateId,
        requestType: REQUEST_TYPE.TEMPLATE
      });
    };

    const beginStart = () => {
      if (!isAppOnline()) {
        continueStart();
        return;
      }

      this.setState({ fetchingTemplateInfo: true });
      this.getTemplateInfo(templateId, (templateInfo, response, fromCache) => {
        this.setState({ fetchingTemplateInfo: false });

        if (!templateInfo || isTemplateNotFoundResponse(response)) {
          this.openTemplateNotFoundDialog(templateId);
          return;
        }

        if (
          !fromCache &&
          window.standalone &&
          response &&
          !response.errorCode &&
          !isTemplateNotFoundResponse(response)
        ) {
          gaeApp('TEMPLATE_CACHE', 'SAVE_ON_START', templateId);
          saveCachedTemplate(templateId, response);
        }

        continueStart();
      });
    };

    const proceedWithDraftCheck = () => {
      if (!window.standalone) {
        beginStart();
        return;
      }

      if (fresh) {
        gaeApp('STANDALONE_DRAFT', 'RESTART_FROM_MENU', templateId);
        clearStandaloneDraft().then(() => {
          this.setState(state => ({
            pendingDraft:
              state.pendingDraft?.templateId === templateId
                ? null
                : state.pendingDraft,
            draftVersion: state.draftVersion + 1
          }));
          beginStart();
        });
        return;
      }

      getStandaloneDraft().then(draft => {
        if (!draft?.templateId) {
          beginStart();
          return;
        }

        if (draft.templateId === templateId) {
          if (
            draft.pwaSession === window.pwaSession &&
            !hasPendingStandaloneDraftRestore()
          ) {
            markPendingStandaloneDraftRestore();
          }
          beginStart();
          return;
        }

        const draftTemplateName = draft.templateName || draft.templateId;
        gaeApp(
          'STANDALONE_DRAFT',
          'CONFIRM_DISCARD_OTHER_TEMPLATE',
          `${draft.templateId}->${templateId}`
        );
        this.$f7.dialog.confirm(
          II18.get('standalone_draft_discard_other', draftTemplateName),
          II18.get('dialog_warning'),
          () => {
            clearStandaloneDraft().then(beginStart);
          }
        );
      });
    };

    proceedWithDraftCheck();
  };

  startScanner = () => {
    if (!isAppOnline()) {
      return;
    }
    this.setState({ scannerOn: true });
  };

  stopScanner = () => {
    this.setState({ scannerOn: false });
  };

  render() {
    const {
      fetchingTemplateInfo,
      scannerOn,
      pendingDraft,
      installGuideOpened
    } = this.state;

    if (window.isDesktop && !window.standalone) {
      window.location.href = 'https://paperfly.io';
      return '';
    }

    const usedTemplates = window.standalone ? getUsedTemplates() : [];
    const hasUsedTemplates = usedTemplates.length > 0;
    const isDesktopStandalone = window.standalone && window.isDesktop;
    const showScanner =
      isAppOnline() &&
      !fetchingTemplateInfo &&
      !isDesktopStandalone &&
      (!hasUsedTemplates || scannerOn);

    return (
      <Page className={'start-qr'}>
        <Navbar>
          <NavLeft>
            <Link id="aboutApp" onClick={this.openAboutDialog}>
              <Icon ios="f7:info_circle" md="material:info" />
            </Link>
          </NavLeft>
          <NavTitle>Paperfly</NavTitle>
          {this.props.returnFunction && (
            <NavRight>
              <Link
                id={'navigateBack'}
                onClick={() => {
                  gaNavigateBack();
                  setTimeout(this.props.returnFunction, 50);
                }}
              >
                <Icon ios="f7:arrow_right" md="material:arrow_forward" />
              </Link>
            </NavRight>
          )}
        </Navbar>

        {fetchingTemplateInfo && <Spinner fullScreen />}

        {hasUsedTemplates && (
          <div style={{ display: scannerOn ? 'none' : 'block' }}>
            <TemplatesList
              startScanner={this.startScanner}
              startTemplate={this.startTemplate}
              usedTemplates={usedTemplates}
              removeTemplates={this.removeTemplates}
              draftVersion={this.state.draftVersion}
            />
            {isAppOnline() &&
              (isDesktopStandalone ? (
                <DesktopStartRequestFab onSubmit={this.handleRequestData} />
              ) : (
                <Fab
                  position="right-bottom"
                  slot="fixed"
                  onClick={this.startScanner}
                  text={II18.get('qr_start_scan_btn')}
                  color="#2196f3"
                >
                  <Icon f7="qrcode" />
                </Fab>
              ))}
          </div>
        )}

        {isDesktopStandalone && isAppOnline() && !hasUsedTemplates && (
          <DesktopStartRequestFab onSubmit={this.handleRequestData} />
        )}

        {showScanner && (
          <>
            <Scanner
              handleScan={this.handleRequestData}
              stopScanner={this.stopScanner}
              startScanner={this.startScanner}
              showCloseButton={hasUsedTemplates}
              scannerOn={scannerOn}
            />
            {process.env.REACT_APP_ENV !== 'production' && (
              <>
                <p style={{ textAlign: 'center', color: '#666' }}>
                  {II18.get('qr_direkt_request_or')}
                  <br />
                  {II18.get('qr_direkt_request_message')}
                </p>
                <Card style={{ paddingRight: 10 }}>
                  <List noHairlines noHairlinesBetween>
                    <ListInput
                      name={'requestId'}
                      id={'directRequestId'}
                      outline
                      required
                      floatingLabel
                      type="text"
                      clearButton
                      value={this.state.requestId}
                      onChange={e =>
                        this.setState({ requestId: e.target.value })
                      }
                    >
                      <Button
                        id={'btnStartRequestDirekt'}
                        slot={'content-end'}
                        className={'scan-portal'}
                        fill
                        onClick={() => {
                          this.handleRequestData(this.state.requestId);
                          this.setState({ requestId: '' });
                        }}
                      >
                        {getIcon('material:chevron_right', 20)}
                      </Button>
                    </ListInput>
                  </List>
                </Card>
              </>
            )}
          </>
        )}

        {!window.standalone && <Footer mobile />}

        {pendingDraft && !scannerOn && (
          <StandaloneDraftNotification
            templateName={pendingDraft.templateName || pendingDraft.templateId}
            updatedAt={pendingDraft.updatedAt}
            onContinue={this.continuePendingDraft}
            onClose={this.dismissPendingDraft}
          />
        )}

        <InstallUserGuid
          opened={installGuideOpened}
          onClose={() => this.setState({ installGuideOpened: false })}
        />
      </Page>
    );
  }
}

StartFromQR.propTypes = {
  returnFunction: PropTypes.func,
  requestInfo: PropTypes.object
};

export default StartFromQR;
